/**
 * Programs Tab Component
 * Specialty programs and weekly check-ins for Volunteer Management
 */

import React from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Music, Dog, Phone } from "lucide-react";

const ProgramsTab = ({
  isDark,
  specialtyPrograms,
  weeklyCheckins
}) => {
  return (
    <div className="space-y-4">
      {/* Specialty Programs Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {specialtyPrograms.map((program) => (
          <Card key={program.id} className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"} data-testid={`program-card-${program.id}`}>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                {program.id === "threshold_singers" ? 
                  <Music className="w-5 h-5 text-purple-500" /> : 
                  <Dog className="w-5 h-5 text-amber-500" />
                }
                {program.name}
              </CardTitle>
              <CardDescription>{program.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className={isDark ? "text-gray-400" : "text-gray-600"}>Active Volunteers</span>
                  <span className="text-xl font-bold">{program.active_volunteers}</span>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1">
                    View Volunteers
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1">
                    Eligible Patients
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Weekly Check-in List */}
      <Card className={isDark ? "bg-slate-900 border-slate-800" : "bg-white"}>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Phone className="w-5 h-5 text-teal-500" />
            Weekly Check-in Call List
          </CardTitle>
          <CardDescription>
            Volunteers needing engagement calls
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {weeklyCheckins.map((checkin, idx) => (
              <div 
                key={idx}
                className={`p-3 rounded-lg flex items-center justify-between ${isDark ? "bg-slate-800" : "bg-gray-50"}`}
                data-testid={`checkin-item-${idx}`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-2 h-2 rounded-full ${checkin.priority === "high" ? "bg-red-500" : "bg-yellow-500"}`} />
                  <div>
                    <p className="font-medium">{checkin.volunteer_name}</p>
                    <p className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                      {checkin.phone || checkin.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    {checkin.days_since_last_checkin ? `${checkin.days_since_last_checkin} days ago` : "Never"}
                  </span>
                  <Button size="sm" variant="outline">
                    <Phone className="w-4 h-4 mr-1" />
                    Call
                  </Button>
                </div>
              </div>
            ))}
            {weeklyCheckins.length === 0 && (
              <p className={`text-center py-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}>
                All volunteers have been contacted recently
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProgramsTab;
